import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  EnvelopeSimpleIcon
} from "@phosphor-icons/react";

import {
  selectCompanies,
  selectCompanyInfoDetails
} from "pages/Companies/slice/selector";
import {
  ButtonsWrapperStyles,
  CompanyTabsWrapper
} from "pages/RosterStatusPage/styled";
import { CompaniesWrapperStyles } from "pages/Companies/styled";
import CompanyTabs from "pages/RosterStatusPage/CompanyTabs";
import { useCompaniesSlice } from "pages/Companies/slice/index";
import ContactSocial from "pages/RosterStatusPage/ContactSocialTabs";

import Icon from "components/Icon/Icon";
import Button from "components/Buttons/index";
import LoadingSpinner from "components/PageLoader/Spinner";
import SkeletonLoader from "components/PageLoader/SkeletonLoader";

import { CompanyNameCell, renderValue } from "utils/renderCompanyCell";

import { CommonDashboardWrapper } from "styles/GlobalStyles";

const RosterStatusPage = () => {
  const { actions } = useCompaniesSlice();
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      page: params.get("page") || 1,
      limit: params.get("limit") || 10,
      sort_order: params.get("sort_order") || "asc",
      sort_by: params.get("sort_by") || "",
      search: params.get("search") || "",
      status: params.get("status") || ""
    };
  }, [location.search]);
  const prevSearch = location.search || "";

  const { companies, loading: companiesLoading } = useSelector(selectCompanies);
  const infoDetails = useSelector(selectCompanyInfoDetails);

  useEffect(() => {
    if (!infoDetails.size?.length) dispatch(actions.fetchCompanyInfoDetails());
    if (!companies || companies.length === 0) {
      dispatch(actions.fetchCompanies(queryParams));
    }
  }, []);

  const activeCompany = useMemo(() => {
    if (!companies?.length) return null;
    return companies.find((c) => c.id.toString() === companyId);
  }, [companies, companyId]);

  const isLoading =
    companiesLoading ||
    !companies?.length ||
    !infoDetails?.size?.length ||
    !activeCompany;

  const tabs = [
    {
      id: "company-info",
      label: "Company Information",
      icon: (
        <Icon
          component={BuildingOfficeIcon}
          color="#4C4C4C"
          format="outline"
          weight="regular"
        />
      )
    },
    {
      id: "contact-social",
      label: "Contact & Social Details",
      icon: (
        <Icon
          component={EnvelopeSimpleIcon}
          color="#4C4C4C"
          format="outline"
          weight="regular"
        />
      )
    }
  ];

  const getTabFromURL = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    return tabs.some((t) => t.id === tab) ? tab : tabs[0].id;
  };

  const [activeTab, setActiveTab] = useState(getTabFromURL());
  const [isChanged, setIsChanged] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const currentTab = getTabFromURL();
    if (currentTab !== activeTab) setActiveTab(currentTab);
  }, [location.search]);

  useEffect(() => {
    if (companyId) {
      const currentTab = getTabFromURL();
      if (!tabs.some((t) => t.id === currentTab)) {
        const params = new URLSearchParams(location.search);
        params.set("tab", tabs[0].id);
        navigate(
          { pathname: location.pathname, search: params.toString() },
          { replace: true }
        );
        setActiveTab(tabs[0].id);
      }
    }
  }, [companyId]);

  const normalizedCompany = useMemo(() => {
    if (!activeCompany) return null;
    return {
      ...activeCompany,
      services: activeCompany.services?.map((s) => s.id) || [],
      specialized_regions: (activeCompany.specialized_regions || [])
        .map((r) => (typeof r === "string" ? r : r?.name || r?.value))
        .filter(Boolean),
      specialized_in: activeCompany.specialized_in?.[0]?.id || null,
      exclude_weekly_ta_mails: Boolean(activeCompany.exclude_weekly_ta_mails)
    };
  }, [activeCompany]);

  useEffect(() => {
    if (normalizedCompany) setFormData(normalizedCompany);
  }, [normalizedCompany]);

  const handleInputChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSelectChange = (field) => (value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.logoPreview) URL.revokeObjectURL(formData.logoPreview); // Clean up previous preview
      const preview = URL.createObjectURL(file); // Create preview URL
      setFormData((prev) => ({
        ...prev,
        logoPreview: preview,
        logoName: file.name, // Store the file name
        file: file // Attach the file to formData
      }));
    }
  };

  const dropdownOptions = useMemo(() => {
    if (!infoDetails) return {};
    return {
      cities:
        infoDetails.cities?.map((c) => ({ label: c.name, value: c.id })) || [],
      industries:
        infoDetails.industries?.map((i) => ({ label: i.name, value: i.id })) ||
        [],
      sizes: infoDetails.size?.map((s) => ({ label: s, value: s })) || [],
      companyServices:
        infoDetails.services?.map((s) => ({ label: s.name, value: s.id })) ||
        [],
      specializedRegions:
        infoDetails.specialized_regions?.map((r) => ({ label: r, value: r })) ||
        [],
      specializedIn:
        infoDetails.specialized_in?.map((r) => ({
          label: r.name,
          value: r.id
        })) || [],
      types: infoDetails.types?.map((t) => ({ label: t, value: t })) || [],
      statuses: infoDetails.status?.map((s) => ({ label: s, value: s })) || []
    };
  }, [infoDetails]);

  const getChangedFields = () => {
    if (!activeCompany) return {};
    const changed = {};
    Object.keys(formData).forEach((key) => {
      if (key === "file") return; // avoid adding file twice

      let oldValue = activeCompany[key];
      const newValue = formData[key];

      if (key === "exclude_weekly_ta_mails") {
        oldValue = Boolean(activeCompany.exclude_weekly_ta_mails);
      }
      // Use IDs for comparison for multi-select fields
      if (key === "services")
        oldValue = activeCompany.services?.map((s) => s.id) || [];
      if (key === "specialized_in") {
        oldValue = activeCompany.specialized_in?.[0]?.id || null;
      }
      if (key === "specialized_regions")
        oldValue = activeCompany.specialized_regions || [];

      const match =
        Array.isArray(oldValue) && Array.isArray(newValue)
          ? JSON.stringify(oldValue) === JSON.stringify(newValue)
          : String(oldValue ?? "") === String(newValue ?? "");

      if (!match) changed[key] = newValue;
    });
    return changed;
  };

  useEffect(() => {
    const changed = getChangedFields();
    setIsChanged(Object.keys(changed).length > 0);
  }, [formData]);

  const handleSave = () => {
    const changedFields = getChangedFields();

    // List of dropdowns to transform from ID → name
    const dropdownMappings = {
      services: dropdownOptions.companyServices, // multi-select
      specialized_in: dropdownOptions.specializedIn, // multi-select
      city: dropdownOptions.cities, // single-select
      industry: dropdownOptions.industries // single-select
    };

    Object.entries(dropdownMappings).forEach(([field, options]) => {
      if (!changedFields[field]) return;

      if (Array.isArray(changedFields[field])) {
        // Multi-select
        changedFields[field] = changedFields[field]
          .map((id) => {
            const match = options.find((opt) => opt.value === id);
            if (!match) return null;
            return { id: match.value, name: match.label };
          })
          .filter(Boolean);
      } else {
        // Single-select — handle specialized_in specially
        const match = options.find((opt) => opt.value === changedFields[field]);
        if (match) {
          const valueObj = { id: match.value, name: match.label };
          if (field === "specialized_in") {
            changedFields[field] = [valueObj]; // wrap in array
          } else {
            changedFields[field] = valueObj;
          }
        }
      }
    });

    // Prepare FormData
    const payload = new FormData();
    if (formData.file instanceof File) payload.append("file", formData.file);

    Object.entries(changedFields).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === "object") {
        payload.append(key, JSON.stringify(value));
      } else {
        payload.append(key, value);
      }
    });

    dispatch(
      actions.updateCompanyDetails({
        companyId: activeCompany.id,
        formData: payload,
        prevSearch,
        navigate
      })
    );
  };

  const handleGoBack = () => {
    // Use the query from URL if state is empty
    const query = location.state?.prevSearch || window.location.search;
    navigate(`/companies${query}`);
  };

  return (
    <CommonDashboardWrapper>
      <CompaniesWrapperStyles>
        {isLoading ? (
          <SkeletonLoader width="600px" height="40px" />
        ) : (
          <div className="go-back-wrapper">
            <div className="go-back-icon" onClick={handleGoBack}>
              <Icon
                component={ArrowLeftIcon}
                format="outline"
                weight="regular"
              />
            </div>
            <div className="update-text-wrapper">
              <h4>Update</h4>
              <CompanyNameCell
                logo={activeCompany?.logo}
                name={renderValue(activeCompany?.company_name)}
                suffix="&lsquo;s"
              />
            </div>
          </div>
        )}
      </CompaniesWrapperStyles>

      <CompanyTabsWrapper className={isLoading ? "loading-wrapper" : ""}>
        {isLoading ? (
          <LoadingSpinner
            color="#797979"
            text="Loading Data..."
            fontSize={16}
          />
        ) : (
          <>
            <div className="tabs-header">
              {tabs?.map((tab) => (
                <div
                  key={tab.id}
                  className={`tab-item ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => {
                    const params = new URLSearchParams(location.search);
                    params.set("tab", tab.id);
                    navigate(
                      {
                        pathname: location.pathname,
                        search: params.toString()
                      },
                      { replace: true }
                    );
                    setActiveTab(tab.id);
                  }}
                >
                  {tab.icon}
                  <span className="tab-label">{tab.label}</span>
                </div>
              ))}
            </div>

            <div className="tab-content">
              {activeTab === "company-info" && (
                <CompanyTabs
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  dropdownOptions={dropdownOptions}
                  handleFileChange={handleFileChange}
                />
              )}

              {activeTab === "contact-social" && (
                <ContactSocial
                  formData={formData}
                  setFormData={setFormData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  dropdownOptions={dropdownOptions}
                />
              )}
            </div>

            <ButtonsWrapperStyles>
              <Button variant="secondary" onClick={handleGoBack}>
                Cancel
              </Button>
              <Button
                size="M"
                variant="primary"
                onClick={handleSave}
                disabled={!isChanged}
              >
                Save Changes
              </Button>
            </ButtonsWrapperStyles>
          </>
        )}
      </CompanyTabsWrapper>
    </CommonDashboardWrapper>
  );
};

export default RosterStatusPage;
